/**
 * FilterBar — Controlled Component fuer Board-Filter (BOARD-03, BOARD-04).
 *
 * Layout:
 *  - Such-Input (debounced 300ms intern, OnChange-Throttling fuer leadsApi)
 *  - Filter-Dropdowns: Tag (Multi-Select), Assigned-User, Zeitraum
 *  - Aktive Filter als entfernbare Chips
 *
 * Pattern: kein interner State fuer filters — parent haelt BoardFilters,
 * onChange feuert mit Partial-Patch. EINE Ausnahme: Such-Input bekommt einen
 * lokalen "draft"-State fuer den Debounce.
 *
 * CI-Farben: #E9FF57 (Gelb), #1A1A1A (Schwarz), #DAD9D9 (Grau).
 * NIE weisse Schrift auf gelbem Hintergrund.
 */

import { useEffect, useRef, useState } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import type { BoardFilters, DateRange, UserMini } from '../types';

interface FilterBarProps {
  filters: BoardFilters;
  onChange: (patch: Partial<BoardFilters>) => void;
  users: UserMini[];
  allTags: string[];
}

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  '7d': 'Letzte 7 Tage',
  '30d': 'Letzte 30 Tage',
  '90d': 'Letzte 90 Tage',
  all: 'Alle',
};

export default function FilterBar({ filters, onChange, users, allTags }: FilterBarProps) {
  // Lokaler Draft fuer Such-Input damit das Tippen nicht jedes Zeichen die
  // gefilterte Liste flackert. setTimeout-Debounce 300ms.
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Wenn Filter von aussen (z.B. Reset) geaendert wird, Draft mitziehen.
    setSearchDraft(filters.search);
  }, [filters.search]);

  const handleSearchChange = (val: string) => {
    setSearchDraft(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onChange({ search: val });
    }, 300);
  };

  const [tagOpen, setTagOpen] = useState(false);

  const toggleTag = (tag: string) => {
    const next = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onChange({ tags: next });
  };

  const activeUser = users.find((u) => u.id === filters.assignedUserId);

  return (
    <div className="border border-[#DAD9D9] rounded-lg p-4 bg-white space-y-3">
      {/* Such-Input */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchDraft}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Vereinsname oder E-Mail suchen..."
          className="w-72 pl-9 pr-3 py-2 text-sm border border-[#DAD9D9] rounded focus:outline-none focus:border-[#1A1A1A] text-[#1A1A1A]"
        />
      </div>

      {/* Dropdown-Reihe */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Tag Multi-Select */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setTagOpen((v) => !v)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#DAD9D9] rounded hover:bg-gray-50 text-[#1A1A1A]"
          >
            Tags
            {filters.tags.length > 0 && (
              <span className="text-xs text-gray-500">({filters.tags.length})</span>
            )}
            <ChevronDown className="w-3 h-3" />
          </button>
          {tagOpen && (
            <div className="absolute z-50 mt-1 bg-white border border-[#DAD9D9] rounded shadow-lg p-2 space-y-1 min-w-[200px] max-h-80 overflow-auto">
              {allTags.length === 0 && (
                <div className="text-xs text-gray-400 px-2 py-1">Keine Tags vorhanden</div>
              )}
              {allTags.map((tag) => (
                <label
                  key={tag}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded text-[#1A1A1A]"
                >
                  <input
                    type="checkbox"
                    checked={filters.tags.includes(tag)}
                    onChange={() => toggleTag(tag)}
                  />
                  <span>{tag}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Assigned-User Select */}
        <select
          value={filters.assignedUserId ?? ''}
          onChange={(e) =>
            onChange({
              assignedUserId: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className="px-3 py-1.5 text-sm border border-[#DAD9D9] rounded bg-white text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A]"
        >
          <option value="">Alle User</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name ?? u.email}
            </option>
          ))}
        </select>

        {/* Zeitraum Select */}
        <select
          value={filters.dateRange}
          onChange={(e) => onChange({ dateRange: e.target.value as DateRange })}
          className="px-3 py-1.5 text-sm border border-[#DAD9D9] rounded bg-white text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A]"
        >
          {(Object.entries(DATE_RANGE_LABELS) as [DateRange, string][]).map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </select>

        {/* Source Select (D-06) */}
        <select
          value={filters.source ?? ''}
          onChange={(e) => onChange({ source: e.target.value === '' ? null : e.target.value })}
          className="px-3 py-1.5 text-sm border border-[#DAD9D9] rounded bg-white text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A]"
        >
          <option value="">Alle Quellen</option>
          <option value="plenty_form">Mail (Formular)</option>
          <option value="manual">Manuell</option>
          <option value="phone">Telefon</option>
          <option value="other">Sonstige</option>
        </select>

        {/* End-Spalten-Filter Toggle (PR 3, 01.06.) — Default aktiv:
            nur Karten der letzten 90 Tage in den End-Spalten zeigen. */}
        <label
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-[#DAD9D9] rounded bg-white text-[#1A1A1A] cursor-pointer hover:bg-gray-50"
          title="Wenn aktiv: Endspalten (In Kontakt, Teamshop angelegt, Keine Rueckmeldung, KR Hotlead) zeigen nur Karten der letzten 90 Tage."
        >
          <input
            type="checkbox"
            checked={filters.endColumnAgeFilter}
            onChange={(e) => onChange({ endColumnAgeFilter: e.target.checked })}
            className="accent-[#E9FF57]"
          />
          <span>Endspalten: 90 Tage</span>
        </label>
      </div>

      {/* Aktive Filter-Chips */}
      {(filters.tags.length > 0 ||
        filters.assignedUserId != null ||
        filters.dateRange !== 'all' ||
        filters.search.trim().length > 0 ||
        filters.source != null) && (
        <div className="flex flex-wrap gap-2 pt-1">
          {filters.search.trim() && (
            <Chip
              label={`Suche: ${filters.search}`}
              onRemove={() => {
                setSearchDraft('');
                onChange({ search: '' });
              }}
            />
          )}
          {filters.tags.map((tag) => (
            <Chip key={tag} label={`Tag: ${tag}`} onRemove={() => toggleTag(tag)} />
          ))}
          {activeUser && (
            <Chip
              label={`User: ${activeUser.name ?? activeUser.email}`}
              onRemove={() => onChange({ assignedUserId: null })}
            />
          )}
          {filters.dateRange !== 'all' && (
            <Chip
              label={`Zeitraum: ${DATE_RANGE_LABELS[filters.dateRange]}`}
              onRemove={() => onChange({ dateRange: 'all' })}
            />
          )}
          {filters.source && (
            <Chip
              label={`Quelle: ${{ plenty_form: 'Mail', manual: 'Manuell', phone: 'Telefon', other: 'Sonstige' }[filters.source] ?? filters.source}`}
              onRemove={() => onChange({ source: null })}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="bg-[#E9FF57] text-[#1A1A1A] px-2 py-1 rounded text-xs flex items-center gap-1">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="hover:bg-[#d4ea4d] rounded p-0.5"
        aria-label="Filter entfernen"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
